import { ReactNode } from 'react'
import { useOverlay, usePreventScroll, useModal } from '@react-aria/overlays'
import { useDialog } from '@react-aria/dialog'
import { FocusScope } from '@react-aria/focus'
import { useObjectRef } from 'utils/useObjectRef'
import { AriaDialogProps } from '@react-types/dialog'
import type { AriaOverlayProps } from '@react-aria/overlays'
import cx from 'classnames'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'
import Styles from './ModalDialog.module.scss'
import { IconButton } from '../IconButton/IconButton'

interface Props extends AriaOverlayProps, AriaDialogProps {
  children: ReactNode | string
  title: ReactNode | string
  footer?: ReactNode | string
  footerBorder?: boolean
  containFocus?: boolean
  className?: string
}

export const ModalDialog = ({
  className,
  containFocus = true,
  footerBorder = true,
  children,
  title,
  footer,
  isOpen = true,
  onClose,
  isDismissable,
  shouldCloseOnBlur,
  isKeyboardDismissDisabled,
  shouldCloseOnInteractOutside,
  ...dialogAriaProps
}: Props) => {
  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const ref = useObjectRef<HTMLDivElement>()
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      isDismissable,
      shouldCloseOnBlur,
      isKeyboardDismissDisabled,
      shouldCloseOnInteractOutside,
    },
    ref,
  )
  // Prevent scrolling while the modal is open, and hide content
  // outside the modal from screen readers.
  usePreventScroll()
  const { modalProps } = useModal()
  // Get props for the dialog and its title
  const { dialogProps, titleProps } = useDialog(dialogAriaProps, ref)

  return (
    <div className={Styles.underlay} {...underlayProps}>
      <FocusScope contain={containFocus} restoreFocus autoFocus>
        <div {...overlayProps} {...dialogProps} {...modalProps} ref={ref} className={cx(Styles.dialog, className)}>
          <header className={Styles.header}>
            <h3 {...titleProps} className={Styles.heading}>
              {title}
            </h3>
            <IconButton
              icon={CloseIcon}
              variant="tertiary"
              size="lg"
              onPress={onClose}
              className={Styles.button}
            />
          </header>
          <div className={Styles.body}>
            <div className={Styles.innerBody}>{children}</div>
          </div>
          {footer && <footer className={cx(Styles.footer, { [Styles.noBorder]: !footerBorder })}>{footer}</footer>}
        </div>
      </FocusScope>
    </div>
  )
}
